import { Fragment, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import { labelOf } from '@/lib/enumLabels';
import { buildStorefrontProductUrl } from '@/features/catalog/lib/storefrontProductUrl';
import { extractSummaryCharge, formatChargeAmount, formatKrwLedger } from '../lib/chargeMoney';
import { ORDER_MALL_LABELS } from '../lib/orderMall';
import { memberNameOf, ordererNameOf } from '../lib/ordererName';
import { OrderStatusPill } from './OrderStatusPill';
import { ItemRefundNote } from './ItemRefundNote';
import { labelNameForCode } from '../lib/displayLabelCode';
import { formatDateTime, formatMoney, ordererMobileOf, paymentMethodLabelOf, type OrderSummaryExtended } from './OrdersTable';
import { ORDER_COLUMN_LABELS, useOrderColumnPrefs, type ToggleableOrderColumn } from '@/stores/useOrderColumnPrefs';
import { visibleOptionLabel } from '@/lib/catalog/optionLabel';
import { CnyReference } from './CnyReference';

type MoneyLike = { amount: number; currencyCode: string };

/** QA-524 상품 줄(BE `GET /admin/v1/orders?includeLines=true` 의 lines[]). */
export interface OrderLine {
  orderItemId: string;
  /** 상품주문번호 — 이관 주문은 고도몰 orderGoods sno, 신규는 주문 안 일련번호. */
  lineNo?: number | string | null;
  productId?: string | null;
  productName?: string | null;
  optionLabel?: string | null;
  thumbnailUrl?: string | null;
  /** QA-536 자체상품코드(주문 시점) — BE 목록 lines 추가 요청. */
  selfProductCode?: string | null;
  quantity: number;
  unitPrice?: MoneyLike | null;
  lineAmount?: MoneyLike | null;
  /** 외화 주문만 — 청구 통화 환산 금액(목록 charge 와 같은 형식, amount 는 소수 문자열). */
  chargeLineAmount?: { amount: string; currencyCode: string } | null;
  /** 이 상품의 취소된 수량(부분 취소 승인·취소 클레임 완료). 일부만 취소되면 상태는 진행 중이고 수량 옆에 표시. */
  canceledQuantity?: number | null;
  statusLabelCode?: string | null;
  statusLabel?: string | null;
}

export type OrderWithLines = OrderSummaryExtended & { lines?: ReadonlyArray<OrderLine> | null };

const money = (m: MoneyLike | null | undefined): string => {
  if (!m) return '-';
  return m.currencyCode === 'KRW' ? formatKrwLedger(m.amount) : formatMoney(m);
};

const cellStyle = { borderBottom: '1px solid var(--divider-default, var(--divider-strong))', verticalAlign: 'middle' } as const;
const mergedStyle = { ...cellStyle, borderRight: '1px solid var(--divider-default, var(--divider-strong))' } as const;

/** QA-536 칸 무리 — 주문 단위(합친 칸) 왼쪽/오른쪽, 상품 줄 단위. 상태는 상품별이라 맨 끝 줄 칸. */
const ORDER_LEFT: ReadonlyArray<ToggleableOrderColumn> = ['orderedAt', 'mall', 'ordererName', 'ordererEmail', 'ordererMobile'];
const LINE_COLS: ReadonlyArray<ToggleableOrderColumn> = ['product', 'quantity', 'selfProductCode', 'unitPrice'];
const ORDER_RIGHT: ReadonlyArray<ToggleableOrderColumn> = [
  'paymentMethod',
  'paymentAmount',
  'totalDiscountAmount',
  'totalOrderAmount',
  'shippingFee',
  'payableAmount',
  'fulfillmentType',
];

/**
 * QA-524 주문 목록 [상품주문번호별] — 주문 상품마다 한 줄(고도몰 상품주문번호별 목록).
 * 같은 주문의 체크박스·주문번호·국가몰·주문일시·구매자·배송비·총 주문금액은 셀을 합쳐 한 번만, 상품 칸·상품별 주문상태는 줄마다.
 * 줄을 누르면 주문 상세, 썸네일은 FE 상품 상세(새 탭), 상품명은 BO 상품 상세. 선택·일괄 처리는 주문 단위.
 * BE 가 lines 를 아직 안 주면 대표 상품 + "외 N건" 한 줄로 보여 준다.
 */
export const OrderItemsListTable = ({
  items,
  selectedIds,
  onToggleRow,
  onToggleAll,
  lineFilter,
}: {
  items: ReadonlyArray<OrderWithLines>;
  /** QA-611 자체상품코드 검색 — 일치하는 상품 줄만. 맞는 줄이 없으면(코드 표기 차이 등) 주문의 줄을 모두 보인다. */
  lineFilter?: ((line: OrderLine) => boolean) | undefined;
  selectedIds: ReadonlyArray<string>;
  onToggleRow: (orderId: string) => void;
  onToggleAll: (ids: ReadonlyArray<string>) => void;
}) => {
  const { t } = useTranslation('orders');
  const navigate = useNavigate();
  const allIds = items.map((i) => i.orderId);
  const allChecked = allIds.length > 0 && allIds.every((id) => selectedIds.includes(id));
  const linesReady = items.some((o) => Array.isArray(o.lines));
  const visibleColumns = useOrderColumnPrefs((st) => st.visibleColumns);

  if (items.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-[color:var(--content-muted)]" data-testid="orders-empty">
        {t('orders.list.empty')}
      </p>
    );
  }

  const go = (orderId: string) => navigate(`/orders/${orderId}`);
  const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();
  // QA-536 노출 항목 — 같은 설정을 주문별 목록과 함께 쓴다. 칸 무리: 주문 왼쪽(합침) · 상품 줄 · 주문 오른쪽(합침) · 상태(줄).
  const show = new Set(visibleColumns);
  const left = ORDER_LEFT.filter((c) => show.has(c));
  const lineCols = LINE_COLS.filter((c) => show.has(c));
  const right = ORDER_RIGHT.filter((c) => show.has(c));
  const showStatus = show.has('status');
  const dash = <span style={{ color: 'var(--content-muted)' }}>-</span>;

  const orderCell = (order: OrderWithLines, c: ToggleableOrderColumn): ReactNode => {
    switch (c) {
      case 'orderedAt':
        return formatDateTime(order.orderedAt);
      case 'mall':
        return labelOf(ORDER_MALL_LABELS, order.mall ?? undefined);
      case 'ordererName': {
        const buyer = ordererNameOf(order) ?? '-';
        const member = memberNameOf(order);
        return (
          <>
            <div className="max-w-[140px] truncate font-medium" style={{ color: 'var(--content-strong)' }} title={buyer}>
              {buyer}
            </div>
            {member ? (
              <div className="max-w-[140px] truncate text-xs" style={{ color: 'var(--content-muted)' }} title={member}>
                회원명 {member}
              </div>
            ) : null}
          </>
        );
      }
      case 'ordererEmail':
        return order.buyerEmail ? (
          <span className="block max-w-[180px] truncate text-xs" title={order.buyerEmail}>
            {order.buyerEmail}
          </span>
        ) : (
          dash
        );
      case 'ordererMobile': {
        const mobile = ordererMobileOf(order);
        return mobile ? <span className="whitespace-nowrap text-xs">{mobile}</span> : dash;
      }
      case 'paymentMethod':
        return paymentMethodLabelOf(order) ?? dash;
      case 'paymentAmount': {
        const charge = extractSummaryCharge(order);
        return charge ? (
          <>
            <div className="font-medium" style={{ color: 'var(--content-strong)' }}>
              {formatChargeAmount(charge.amount, charge.currencyCode)}
              <CnyReference amount={charge.amount} currencyCode={charge.currencyCode} mall={order.mall} />
            </div>
            <div className="text-xs" style={{ color: 'var(--content-muted)' }}>
              {money(order.paymentAmount)}
            </div>
          </>
        ) : (
          money(order.paymentAmount)
        );
      }
      case 'totalDiscountAmount':
        return order.totalDiscountAmount ? money(order.totalDiscountAmount) : dash;
      case 'totalOrderAmount':
        return order.totalOrderAmount ? money(order.totalOrderAmount) : dash;
      case 'shippingFee':
        return money(order.shippingFee);
      case 'payableAmount':
        // order #124 — 실 결제금액 = 목록 paymentAmount(BE 회신).
        return money(order.payableAmount ?? order.paymentAmount);
      case 'fulfillmentType':
        return t(`orders.fulfillmentType.${order.fulfillmentType}`);
      default:
        return null;
    }
  };
  const isMoney = (c: ToggleableOrderColumn) =>
    c === 'paymentAmount' || c === 'totalDiscountAmount' || c === 'totalOrderAmount' || c === 'shippingFee' || c === 'payableAmount';

  return (
    <div className="flex flex-col gap-2">
      {!linesReady ? (
        <p role="status" className="rounded-md p-3 text-[13px]" style={{ background: 'var(--status-warn-bg)', color: 'var(--status-warn-fg)' }} data-testid="order-items-not-ready">
          상품별 목록은 서버 반영 후 주문 상품마다 한 줄로 나옵니다. 지금은 대표 상품만 보입니다.
        </p>
      ) : null}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1200px] border-collapse text-[13px]" data-testid="order-items-table">
          <thead style={{ background: 'var(--surface-1)' }}>
            {/* QA-536 — 머리글은 한 줄(세로 꺾임 없이), 표는 가로 스크롤. */}
            <tr className="whitespace-nowrap text-left text-[12px]" style={{ color: 'var(--content-muted)' }}>
              <th className="px-3 py-2" style={{ width: 44 }}>
                <input type="checkbox" aria-label={t('orders.list.selectAll')} checked={allChecked} onChange={() => onToggleAll(allChecked ? [] : allIds)} />
              </th>
              <th className="px-2 py-2">{t('orders.columns.orderNo')}</th>
              {left.map((c) => (
                <th key={c} className="px-2 py-2">{ORDER_COLUMN_LABELS[c]}</th>
              ))}
              <th className="px-2 py-2">상품주문번호</th>
              {lineCols.map((c) => (
                <th key={c} className={`px-2 py-2 ${c === 'quantity' || c === 'unitPrice' ? 'text-right' : ''}`}>
                  {ORDER_COLUMN_LABELS[c]}
                </th>
              ))}
              <th className="px-2 py-2 text-right">총상품금액</th>
              {right.map((c) => (
                <th key={c} className={`px-2 py-2 ${isMoney(c) ? 'text-right' : ''}`}>{ORDER_COLUMN_LABELS[c]}</th>
              ))}
              {showStatus ? <th className="px-2 py-2">{ORDER_COLUMN_LABELS.status}</th> : null}
            </tr>
          </thead>
          <tbody>
            {items.map((order) => {
              const matched = lineFilter && Array.isArray(order.lines) ? order.lines.filter(lineFilter) : [];
              const lines: ReadonlyArray<OrderLine> =
                matched.length > 0
                  ? matched
                  : Array.isArray(order.lines) && order.lines.length > 0
                  ? order.lines
                  : [
                      {
                        orderItemId: `${order.orderId}-rep`,
                        productName: order.representativeItem.productName,
                        optionLabel: visibleOptionLabel(order.representativeItem.variantLabel),
                        thumbnailUrl: order.representativeItem.thumbnailUrl ?? null,
                        selfProductCode: order.representativeItem.selfProductCode ?? null,
                        unitPrice: order.representativeItem.unitPrice ?? null,
                        quantity: 0,
                      } as OrderLine,
                    ];
              const span = lines.length;
              const isSelected = selectedIds.includes(order.orderId);
              return (
                <Fragment key={order.orderId}>
                  {lines.map((line, i) => (
                    <tr
                      key={line.orderItemId}
                      className="cursor-pointer hover:bg-[color:var(--surface-1)]"
                      onClick={() => go(order.orderId)}
                      data-testid="order-item-row"
                      data-order-id={order.orderId}
                      data-selected={isSelected ? 'true' : 'false'}
                    >
                      {i === 0 ? (
                        <>
                          <td rowSpan={span} className="px-3 py-2" style={mergedStyle} onClick={stop}>
                            <input
                              type="checkbox"
                              aria-label={t('orders.list.selectRow', { orderNo: order.orderNo })}
                              checked={isSelected}
                              onChange={() => onToggleRow(order.orderId)}
                            />
                          </td>
                          <td rowSpan={span} className="px-2 py-2" style={mergedStyle}>
                            <span className="block max-w-[150px] truncate" title={order.orderNo}>
                              {order.orderNo}
                            </span>
                          </td>
                          {left.map((c) => (
                            <td
                              key={c}
                              rowSpan={span}
                              className="whitespace-nowrap px-2 py-2"
                              style={mergedStyle}
                              data-testid={`order-item-col-${c}`}
                            >
                              {orderCell(order, c)}
                            </td>
                          ))}
                        </>
                      ) : null}
                      <td className="px-2 py-2 tabular-nums text-[12px]" style={cellStyle}>
                        {line.lineNo ?? (linesReady ? i + 1 : '-')}
                      </td>
                      {lineCols.map((c) => {
                        if (c === 'product')
                          return (
                            <td key={c} className="px-2 py-2" style={cellStyle}>
                              <div className="flex items-center gap-2">
                                {line.thumbnailUrl && line.productId ? (
                                  <a
                                    href={buildStorefrontProductUrl(line.productId)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={stop}
                                    title="쇼핑몰 상품 상세(새 탭)"
                                    data-testid="order-item-thumb"
                                  >
                                    <img src={line.thumbnailUrl} alt="" className="h-9 w-9 shrink-0 rounded object-cover" style={{ border: '1px solid var(--divider-strong)' }} />
                                  </a>
                                ) : null}
                                <div className="min-w-0">
                                  {line.productId ? (
                                    <Link
                                      to={`/products/${line.productId}`}
                                      onClick={stop}
                                      className="block max-w-[320px] truncate hover:underline"
                                      style={{ color: 'var(--content-strong)' }}
                                      title={line.productName ?? undefined}
                                      data-testid="order-item-name"
                                    >
                                      {line.productName || '-'}
                                    </Link>
                                  ) : (
                                    <span className="block max-w-[320px] truncate" title={line.productName ?? undefined}>
                                      {line.productName || '-'}
                                      {!linesReady && order.additionalItemCount > 0 ? (
                                        <span className="ml-1 text-[color:var(--content-muted)]">
                                          {t('orders.list.additionalCount', { count: order.additionalItemCount })}
                                        </span>
                                      ) : null}
                                    </span>
                                  )}
                                  {visibleOptionLabel(line.optionLabel) ? (
                                    <div className="max-w-[320px] truncate text-xs" style={{ color: 'var(--content-muted)' }}>
                                      {visibleOptionLabel(line.optionLabel)}
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            </td>
                          );
                        if (c === 'quantity')
                          return (
                            <td key={c} className="whitespace-nowrap px-2 py-2 text-right tabular-nums" style={cellStyle} data-testid="order-item-qty">
                              {line.quantity || '-'}
                              {line.canceledQuantity ? (
                                <span className="ml-1 text-xs" style={{ color: 'var(--signal-danger)' }}>
                                  (취소 {line.canceledQuantity})
                                </span>
                              ) : null}
                            </td>
                          );
                        if (c === 'selfProductCode')
                          return (
                            <td key={c} className="px-2 py-2 text-xs" style={cellStyle} data-testid="order-item-self-product-code">
                              {line.selfProductCode || dash}
                            </td>
                          );
                        return (
                          <td key={c} className="px-2 py-2 text-right tabular-nums" style={cellStyle}>
                            {money(line.unitPrice)}
                          </td>
                        );
                      })}
                      <td className="px-2 py-2 text-right tabular-nums" style={cellStyle} data-testid="order-item-line-amount">
                        {line.chargeLineAmount ? (
                          <>
                            <div className="font-medium" style={{ color: 'var(--content-strong)' }}>
                              {formatChargeAmount(line.chargeLineAmount.amount, line.chargeLineAmount.currencyCode)}
                              <CnyReference amount={line.chargeLineAmount.amount} currencyCode={line.chargeLineAmount.currencyCode} mall={order.mall} />
                            </div>
                            <div className="text-xs" style={{ color: 'var(--content-muted)' }}>
                              {money(line.lineAmount)}
                            </div>
                          </>
                        ) : (
                          money(line.lineAmount)
                        )}
                      </td>
                      {i === 0
                        ? right.map((c, ri) => (
                            <td
                              key={c}
                              rowSpan={span}
                              className={`whitespace-nowrap px-2 py-2 ${isMoney(c) ? 'text-right tabular-nums' : ''}`}
                              style={ri === 0 ? { ...mergedStyle, borderLeft: mergedStyle.borderRight } : mergedStyle}
                              data-testid={c === 'paymentAmount' ? 'order-item-total' : `order-item-col-${c}`}
                            >
                              {orderCell(order, c)}
                            </td>
                          ))
                        : null}
                      {showStatus ? (
                        <td className="px-2 py-2" style={cellStyle} data-testid="order-item-status">
                          {/* 상품별 주문상태 — BE 가 주면 그 값, 아니면 주문 상태. */}
                          {line.statusLabelCode || line.statusLabel ? (
                            <span className="text-[12px] font-medium">
                              {line.statusLabelCode ? labelNameForCode(line.statusLabelCode, line.statusLabel ?? line.statusLabelCode) : line.statusLabel}
                            </span>
                          ) : (
                            <OrderStatusPill
                              displayStatus={order.displayStatus}
                              displayLabelCode={(order as { displayLabelCode?: string | null }).displayLabelCode}
                              orderId={order.orderId}
                            />
                          )}
                          {/* QA-625 결제 관리 상품 환불 — 환불완료·부분환불(나머지 수량은 위 상태대로). */}
                          <ItemRefundNote item={line} testId={`order-item-refund-${line.orderItemId}`} />
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
